/**
 * Drives the Higher or Lower screen: starts a run, submits higher/lower guesses, and sequences the
 * reveal → advance (or reveal → run-over) transitions. The server is the source of truth for scoring,
 * coins, and the best run; the challenger's value only arrives in the guess response.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useIsFocused } from '@react-navigation/native';
import { useApiClient, extractErrorMessage } from '../../../helpers/apiClient.helper';
import { useEconomyStore } from '../../shared/stores/economyStore';
import { useInvalidateXpSummary } from '../../economy/hooks/useXpSummary';
import { useAnalytics } from '../../analytics/useAnalytics';
import { ANALYTICS_EVENTS } from '../../analytics/events';
import { startHiloRun, submitHiloGuess } from '../helpers/higherLowerApi';
import type { HiloPlayerCard } from '../helpers/higherLowerApi';
import { HILO_DAILY_COIN_CAP, HILO_GAME_KEY, type HiloChoice } from '../constants/higherLower.constants';

type Phase = 'loading' | 'error' | 'playing' | 'revealing' | 'over';

/** How long the revealed value stays before advancing / showing the run-over overlay. */
const REVEAL_ADVANCE_MS = 950;
const REVEAL_OVER_MS = 1100;
const TOAST_MS = 2200;
/**
 * After the games screen regains focus (e.g. switching back from another tab), swallow guesses for a
 * brief settle window. The screen stays mounted while backgrounded, so a stray pointer/click that the
 * browser replays on focus-return must not register as a real higher/lower choice.
 */
const FOCUS_SETTLE_MS = 400;

interface RevealState {
  value: number;
  correct: boolean;
}

export function useHigherLowerGame() {
  const apiClient = useApiClient();
  const syncCoins = useEconomyStore((s) => s.syncCoins);
  const invalidateXp = useInvalidateXpSummary();
  const isFocused = useIsFocused();
  const { track } = useAnalytics();

  // Guesses are ignored until the screen has been focused for FOCUS_SETTLE_MS (see constant above).
  const acceptsInputRef = useRef(false);
  const settleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (settleTimer.current) clearTimeout(settleTimer.current);
    if (!isFocused) {
      acceptsInputRef.current = false;
      return;
    }
    acceptsInputRef.current = false;
    settleTimer.current = setTimeout(() => {
      acceptsInputRef.current = true;
    }, FOCUS_SETTLE_MS);
    return () => {
      if (settleTimer.current) clearTimeout(settleTimer.current);
    };
  }, [isFocused]);

  const [phase, setPhase] = useState<Phase>('loading');
  const [baseline, setBaseline] = useState<HiloPlayerCard | null>(null);
  const [challenger, setChallenger] = useState<HiloPlayerCard | null>(null);
  const [streak, setStreak] = useState(0);
  const [reveal, setReveal] = useState<RevealState | null>(null);
  const [coinsLeftToday, setCoinsLeftToday] = useState(HILO_DAILY_COIN_CAP);
  const [capReached, setCapReached] = useState(false);
  const [coinsBeat, setCoinsBeat] = useState(0); // the "+N" that floats up on a paid correct guess
  const [bestRun, setBestRun] = useState(0);
  const [newBest, setNewBest] = useState(false);
  const [finalStreak, setFinalStreak] = useState(0);
  const [toast, setToast] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const advanceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimers = useCallback(() => {
    if (advanceTimer.current) clearTimeout(advanceTimer.current);
  }, []);
  useEffect(() => () => {
    if (advanceTimer.current) clearTimeout(advanceTimer.current);
    if (toastTimer.current) clearTimeout(toastTimer.current);
  }, []);

  const flashToast = useCallback((message: string) => {
    setToast(message);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), TOAST_MS);
  }, []);

  const start = useCallback(async () => {
    clearTimers();
    setPhase('loading');
    setReveal(null);
    setCoinsBeat(0);
    setNewBest(false);
    try {
      const round = await startHiloRun(apiClient);
      setBaseline(round.baseline);
      setChallenger(round.challenger);
      setStreak(round.streak);
      setCoinsLeftToday(round.coinsLeftToday);
      setCapReached(round.capReached);
      setBestRun(round.bestRun);
      setPhase('playing');
    } catch (err) {
      setErrorMessage(extractErrorMessage(err, "Couldn't start a round"));
      setPhase('error');
    }
  }, [apiClient, clearTimers]);

  useEffect(() => {
    void start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onGuess = useCallback(async (choice: HiloChoice) => {
    if (!acceptsInputRef.current) return;
    if (busy || phase !== 'playing' || !baseline || !challenger) return;
    setBusy(true);
    try {
      const result = await submitHiloGuess(apiClient, choice);
      setReveal({ value: result.challengerValue, correct: result.correct });
      setPhase('revealing');

      if (result.correct) {
        if (typeof result.coins === 'number') syncCoins(result.coins);
        setCoinsBeat(result.coinsAwarded);
        const wasCapped = capReached;
        advanceTimer.current = setTimeout(() => {
          setBaseline(result.next.baseline);
          setChallenger(result.next.challenger);
          setStreak(result.streak);
          setCoinsLeftToday(result.coinsLeftToday);
          setCapReached(result.capReached);
          setReveal(null);
          setCoinsBeat(0);
          setPhase('playing');
          setBusy(false);
          if (result.capReached && !wasCapped) {
            flashToast("You've banked today's coins — keep going to beat your record! 🔥");
          }
        }, REVEAL_ADVANCE_MS);
      } else {
        // HILO awards its daily-completion XP only when the run ends (server: `!correct`),
        // so refetch here — not on correct guesses — for the header/"+X XP" flash to surface.
        invalidateXp();
        track(ANALYTICS_EVENTS.GAME_COMPLETED, {
          game_key: HILO_GAME_KEY,
          streak: result.streak,
          best_run: result.bestRun,
        });
        advanceTimer.current = setTimeout(() => {
          setFinalStreak(result.streak);
          setBestRun(result.bestRun);
          setNewBest(result.newBest);
          setCoinsLeftToday(result.coinsLeftToday);
          setPhase('over');
          setBusy(false);
        }, REVEAL_OVER_MS);
      }
    } catch (err) {
      flashToast(extractErrorMessage(err, 'Could not submit guess'));
      setBusy(false);
    }
  }, [apiClient, busy, phase, baseline, challenger, capReached, syncCoins, invalidateXp, flashToast, track]);

  const onPlayAgain = useCallback(() => { void start(); }, [start]);

  return {
    phase,
    isLoading: phase === 'loading',
    isError: phase === 'error',
    errorMessage,
    baseline,
    challenger,
    statLabel: baseline?.statLabel ?? '',
    streak,
    reveal,
    coinsLeftToday,
    capReached,
    coinsBeat,
    bestRun,
    newBest,
    finalStreak,
    toast,
    busy,
    onGuess,
    onPlayAgain,
    retry: start,
  };
}

export type UseHigherLowerGame = ReturnType<typeof useHigherLowerGame>;
